import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  execute,
  createEnvironment,
  Environment,
  CalcValue,
  CalculationResult,
  safeExecute,
  isAsyncOperation,
} from "@quanta/calcscript";
import type { PlotData, AsyncOperation, OutputSegment } from "@quanta/calcscript";
import { track } from "../lib/track";
import {
  createWorkspaceIOContext,
  handleAsyncOperation,
} from "../api/workspaceIO";

export interface HistoryEntry {
  id: string;
  input: string;
  output: string;
  result: CalcValue;
  type: CalculationResult["type"];
  latex?: string;
  timestamp: number;
  sessionId: string;
  isError?: boolean;
  errorMessage?: string;
  plots?: PlotData[];
  // Ordered text/plot segments for multi-statement blocks (interleaved render).
  segments?: OutputSegment[];
}

export interface Session {
  id: string;
  name: string;
  color: string;
  position: number;
  currentInput: string;
  inputHistory: string[]; // For arrow-key navigation
  inputHistoryIndex: number;
  isArchived: boolean;
  createdAt: number;
  updatedAt: number;
  lastAccessedAt: number;
}

export interface CalculationSequence {
  id: string;
  name: string;
  entries: string[]; // References to history entry IDs
  createdAt: number;
  updatedAt: number;
}

// On-disk format for a session saved to the workspace (`*.quanta.json`).
export interface SessionFileV1 {
  quantaSession: 1;
  name: string;
  color: string;
  inputHistory: string[];
  // History entries without runtime-only ids (id/sessionId are reassigned on import).
  history: Omit<HistoryEntry, "id" | "sessionId">[];
  savedAt: number;
}

interface CalculatorState {
  // Sessions
  sessions: Session[];
  activeSessionId: string | null;

  // Input (now session-scoped via activeSession)
  cursorPosition: number;

  // History (all sessions)
  history: HistoryEntry[];
  sequences: CalculationSequence[];
  collapsedPlots: string[]; // IDs of collapsed plot entries

  // Environment per session (not persisted directly - rebuilt from history)
  environments: Record<string, Environment>;

  // UI State
  showHistory: boolean;
  showPlot: boolean;
  activeTab:
    | "calculator"
    | "history"
    | "sequences"
    | "marketplace"
    | "files"
    | "settings";
  sessionSidebarCollapsed: boolean;
  showArchivedSessions: boolean;

  // Search
  searchQuery: string;
  searchScope: "all" | "current";
  searchDateFrom: string; // ISO date string or empty
  searchDateTo: string;
  searchErrorsOnly: boolean;
  searchPlotsOnly: boolean;

  // Session Actions
  createSession: (name?: string) => string;
  deleteSession: (id: string) => boolean;
  renameSession: (id: string, name: string) => boolean;
  duplicateSession: (id: string) => string | null;
  switchSession: (id: string) => void;
  archiveSession: (id: string) => void;
  unarchiveSession: (id: string) => void;
  reorderSessions: (ids: string[]) => void;
  updateSessionColor: (id: string, color: string) => void;
  getActiveSession: () => Session | null;

  // Session file (workspace) actions
  exportSessionData: (id: string) => SessionFileV1 | null;
  importSessionData: (data: unknown) => string | null;

  // Input actions (now session-aware)
  setInput: (input: string) => void;
  getInput: () => string;
  setCursorPosition: (position: number) => void;
  insertAtCursor: (text: string) => void;
  navigateInputHistory: (direction: "up" | "down") => void;

  // Evaluation
  evaluate: () => void;
  clearInput: () => void;
  clearHistory: () => void;
  clearSessionHistory: (sessionId?: string) => void;

  // History actions
  addToHistory: (
    entry: Omit<HistoryEntry, "id" | "timestamp" | "sessionId">,
  ) => void;
  deleteHistoryEntry: (id: string) => void;
  reloadHistoryEntry: (id: string) => void;

  // Sequence actions
  createSequence: (name: string, entryIds: string[]) => void;
  deleteSequence: (id: string) => void;
  runSequence: (id: string) => void;

  // UI actions
  setShowHistory: (show: boolean) => void;
  setShowPlot: (show: boolean) => void;
  setActiveTab: (tab: CalculatorState["activeTab"]) => void;
  setSessionSidebarCollapsed: (collapsed: boolean) => void;
  setShowArchivedSessions: (show: boolean) => void;

  togglePlotCollapsed: (id: string) => void;
  isPlotCollapsed: (id: string) => boolean;

  // Search
  setSearchQuery: (query: string) => void;
  setSearchScope: (scope: "all" | "current") => void;
  setSearchDateFrom: (date: string) => void;
  setSearchDateTo: (date: string) => void;
  setSearchErrorsOnly: (enabled: boolean) => void;
  setSearchPlotsOnly: (enabled: boolean) => void;
  getFilteredHistory: () => HistoryEntry[];

  resetEnvironment: () => void;
  getEnvironment: () => Environment;
}

const generateId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Date.now().toString(36)}`;

const SESSION_COLORS = [
  "#6366f1", // Indigo
  "#22d3ee", // Cyan
  "#f59e0b", // Amber
  "#ec4899", // Pink
  "#10b981", // Emerald
  "#8b5cf6", // Violet
  "#ef4444", // Red
  "#3b82f6", // Blue
];

// Helper function to format async operation results
function formatAsyncResult(result: CalcValue): string {
  if (result === null || result === undefined) return "";
  if (typeof result === "number") {
    if (Number.isInteger(result)) return String(result);
    return result.toPrecision(6).replace(/\.?0+$/, "");
  }
  if (typeof result === "string") return result;
  if (Array.isArray(result)) {
    if (result.length === 0) return "[]";
    if (Array.isArray(result[0])) {
      // Matrix
      return result
        .map((row) =>
          (row as CalcValue[]).map((v) => formatAsyncResult(v)).join("  "),
        )
        .join("\n");
    }
    return result.map((v) => formatAsyncResult(v)).join("  ");
  }
  if (typeof result === "object") {
    if ("re" in result && "im" in result) {
      const { re, im } = result as { re: number; im: number };
      if (im === 0) return formatAsyncResult(re);
      if (re === 0) return `${formatAsyncResult(im)}i`;
      const sign = im >= 0 ? "+" : "-";
      return `${formatAsyncResult(re)} ${sign} ${formatAsyncResult(Math.abs(im))}i`;
    }
    // Struct array from dir()
    if (Array.isArray(result)) {
      return result.map((item: any) => item.name || String(item)).join("\n");
    }
    return JSON.stringify(result);
  }
  return String(result);
}

// Helper function to get result type for async operations
function getResultType(result: CalcValue): string {
  if (result === null || result === undefined) return "null";
  if (typeof result === "number") return "number";
  if (typeof result === "string") return "string";
  if (Array.isArray(result)) {
    if (result.length === 0) return "array";
    if (Array.isArray(result[0])) return "matrix";
    return "array";
  }
  if (typeof result === "object") {
    if ("re" in result && "im" in result) return "complex";
    if ("type_" in result) return (result as { type_: string }).type_;
    return "struct";
  }
  return "unknown";
}

const createDefaultSession = (position: number = 0): Session => ({
  id: generateId(),
  name: "Main",
  color: SESSION_COLORS[0],
  position,
  currentInput: "",
  inputHistory: [],
  inputHistoryIndex: -1,
  isArchived: false,
  createdAt: Date.now(),
  updatedAt: Date.now(),
  lastAccessedAt: Date.now(),
});

export const useCalculatorStore = create<CalculatorState>()(
  persist(
    (set, get) => ({
      // Initial state
      sessions: [createDefaultSession()],
      activeSessionId: null, // Will be set on first load
      cursorPosition: 0,
      history: [],
      sequences: [],
      collapsedPlots: [],
      environments: {},
      showHistory: false,
      showPlot: false,
      activeTab: "calculator",
      sessionSidebarCollapsed: false,
      showArchivedSessions: false,
      searchQuery: "",
      searchScope: "current",
      searchDateFrom: "",
      searchDateTo: "",
      searchErrorsOnly: false,
      searchPlotsOnly: false,

      // Session actions
      createSession: (name?: string) => {
        const { sessions } = get();
        const nextPosition =
          Math.max(...sessions.map((s) => s.position), -1) + 1;
        const colorIndex = sessions.length % SESSION_COLORS.length;
        const sessionNumber = sessions.filter((s) => !s.isArchived).length + 1;

        const newSession: Session = {
          id: generateId(),
          name: name || `Session ${sessionNumber}`,
          color: SESSION_COLORS[colorIndex],
          position: nextPosition,
          currentInput: "",
          inputHistory: [],
          inputHistoryIndex: -1,
          isArchived: false,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          lastAccessedAt: Date.now(),
        };

        set((state) => ({
          sessions: [...state.sessions, newSession],
          activeSessionId: newSession.id,
          environments: {
            ...state.environments,
            [newSession.id]: createEnvironment(),
          },
        }));

        return newSession.id;
      },

      deleteSession: (id: string) => {
        const { sessions, activeSessionId, history } = get();

        // Can't delete the last non-archived session
        const nonArchivedSessions = sessions.filter((s) => !s.isArchived);
        if (
          nonArchivedSessions.length <= 1 &&
          !sessions.find((s) => s.id === id)?.isArchived
        ) {
          return false;
        }

        const newSessions = sessions.filter((s) => s.id !== id);
        const newHistory = history.filter((h) => h.sessionId !== id);

        let newActiveSessionId = activeSessionId;
        if (activeSessionId === id) {
          // Switch to adjacent session
          const currentIndex = sessions.findIndex((s) => s.id === id);
          const nextSession =
            newSessions[Math.min(currentIndex, newSessions.length - 1)];
          newActiveSessionId = nextSession?.id || null;
        }

        set((state) => {
          const newEnvironments = { ...state.environments };
          delete newEnvironments[id];
          return {
            sessions: newSessions,
            history: newHistory,
            activeSessionId: newActiveSessionId,
            environments: newEnvironments,
          };
        });

        return true;
      },

      renameSession: (id: string, name: string) => {
        const { sessions } = get();
        const trimmedName = name.trim();

        if (!trimmedName || trimmedName.length > 50) return false;

        // Check for duplicate names
        if (
          sessions.some(
            (s) =>
              s.id !== id && s.name.toLowerCase() === trimmedName.toLowerCase(),
          )
        ) {
          return false;
        }

        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === id
              ? { ...s, name: trimmedName, updatedAt: Date.now() }
              : s,
          ),
        }));

        return true;
      },

      duplicateSession: (id: string) => {
        const { sessions, history } = get();
        const sourceSession = sessions.find((s) => s.id === id);
        if (!sourceSession) return null;

        // Generate unique name
        let newName = `${sourceSession.name} (Copy)`;
        let suffix = 2;
        while (
          sessions.some((s) => s.name.toLowerCase() === newName.toLowerCase())
        ) {
          newName = `${sourceSession.name} (Copy ${suffix++})`;
        }

        const newSessionId = generateId();
        const nextPosition =
          Math.max(...sessions.map((s) => s.position), -1) + 1;

        const newSession: Session = {
          ...sourceSession,
          id: newSessionId,
          name: newName,
          position: nextPosition,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          lastAccessedAt: Date.now(),
        };

        // Duplicate history entries
        const sourceHistory = history.filter((h) => h.sessionId === id);
        const newHistory = sourceHistory.map((h) => ({
          ...h,
          id: generateId(),
          sessionId: newSessionId,
        }));

        set((state) => ({
          sessions: [...state.sessions, newSession],
          history: [...newHistory, ...state.history],
          activeSessionId: newSessionId,
          environments: {
            ...state.environments,
            [newSessionId]: createEnvironment(),
          },
        }));

        return newSessionId;
      },

      switchSession: (id: string) => {
        const { sessions, activeSessionId } = get();
        const session = sessions.find((s) => s.id === id);
        if (!session || session.id === activeSessionId) return;

        set((state) => ({
          activeSessionId: id,
          cursorPosition: 0,
          sessions: state.sessions.map((s) =>
            s.id === id ? { ...s, lastAccessedAt: Date.now() } : s,
          ),
        }));
      },

      archiveSession: (id: string) => {
        const { sessions, activeSessionId } = get();
        const nonArchived = sessions.filter(
          (s) => !s.isArchived && s.id !== id,
        );

        if (nonArchived.length === 0) return; // Can't archive the last session

        let newActiveSessionId = activeSessionId;
        if (activeSessionId === id) {
          newActiveSessionId = nonArchived[0]?.id || null;
        }

        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === id ? { ...s, isArchived: true, updatedAt: Date.now() } : s,
          ),
          activeSessionId: newActiveSessionId,
        }));
      },

      unarchiveSession: (id: string) => {
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === id
              ? { ...s, isArchived: false, updatedAt: Date.now() }
              : s,
          ),
        }));
      },

      reorderSessions: (ids: string[]) => {
        set((state) => ({
          sessions: state.sessions
            .map((s) => ({
              ...s,
              position: ids.indexOf(s.id),
            }))
            .sort((a, b) => a.position - b.position),
        }));
      },

      updateSessionColor: (id: string, color: string) => {
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === id ? { ...s, color, updatedAt: Date.now() } : s,
          ),
        }));
      },

      getActiveSession: () => {
        const { sessions, activeSessionId } = get();
        if (!activeSessionId) {
          // If no active session, use first non-archived session
          const firstSession =
            sessions.find((s) => !s.isArchived) || sessions[0];
          if (firstSession) {
            set({ activeSessionId: firstSession.id });
            return firstSession;
          }
          return null;
        }
        return sessions.find((s) => s.id === activeSessionId) || null;
      },

      // Session file (workspace) actions
      exportSessionData: (id: string) => {
        const { sessions, history } = get();
        const session = sessions.find((s) => s.id === id);
        if (!session) return null;

        const entries = history
          .filter((h) => h.sessionId === id)
          .sort((a, b) => a.timestamp - b.timestamp)
          // Drop runtime-only ids; they are reassigned on import.
          .map(({ id: _id, sessionId: _sid, ...rest }) => rest);

        return {
          quantaSession: 1,
          name: session.name,
          color: session.color,
          inputHistory: session.inputHistory,
          history: entries,
          savedAt: Date.now(),
        };
      },

      importSessionData: (data: unknown) => {
        const file = data as Partial<SessionFileV1> | null;
        if (
          !file ||
          file.quantaSession !== 1 ||
          !Array.isArray(file.history)
        ) {
          return null;
        }

        const { sessions } = get();

        // Ensure a unique, non-empty name.
        const baseName = (file.name || "Imported session").slice(0, 50);
        let name = baseName;
        let suffix = 2;
        while (
          sessions.some((s) => s.name.toLowerCase() === name.toLowerCase())
        ) {
          name = `${baseName} (${suffix++})`;
        }

        const newId = generateId();
        const nextPosition =
          Math.max(...sessions.map((s) => s.position), -1) + 1;
        const colorIndex = sessions.length % SESSION_COLORS.length;

        const newSession: Session = {
          id: newId,
          name,
          color: file.color || SESSION_COLORS[colorIndex],
          position: nextPosition,
          currentInput: "",
          inputHistory: Array.isArray(file.inputHistory)
            ? file.inputHistory
            : [],
          inputHistoryIndex: -1,
          isArchived: false,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          lastAccessedAt: Date.now(),
        };

        const newHistory: HistoryEntry[] = file.history.map((h) => ({
          ...(h as Omit<HistoryEntry, "id" | "sessionId">),
          id: generateId(),
          sessionId: newId,
        }));

        // Rebuild the environment by replaying the successful inputs.
        const env = createEnvironment();
        for (const h of file.history) {
          if (!h.isError && h.input) {
            try {
              execute(h.input, env);
            } catch {
              // Skip entries that no longer evaluate cleanly.
            }
          }
        }

        set((state) => ({
          sessions: [...state.sessions, newSession],
          history: [...newHistory, ...state.history],
          activeSessionId: newId,
          environments: { ...state.environments, [newId]: env },
        }));

        return newId;
      },

      // Input actions (session-aware)
      setInput: (input: string) => {
        const activeSession = get().getActiveSession();
        if (!activeSession) return;

        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === activeSession.id
              ? { ...s, currentInput: input, inputHistoryIndex: -1 }
              : s,
          ),
        }));
      },

      getInput: () => {
        const activeSession = get().getActiveSession();
        return activeSession?.currentInput || "";
      },

      setCursorPosition: (position: number) =>
        set({ cursorPosition: position }),

      insertAtCursor: (text: string) => {
        const { cursorPosition, getInput, setInput } = get();
        const input = getInput();
        const before = input.slice(0, cursorPosition);
        const after = input.slice(cursorPosition);
        const newInput = before + text + after;
        setInput(newInput);
        set({ cursorPosition: cursorPosition + text.length });
      },

      navigateInputHistory: (direction: "up" | "down") => {
        const activeSession = get().getActiveSession();
        if (!activeSession || activeSession.inputHistory.length === 0) return;

        const { inputHistory, inputHistoryIndex } = activeSession;
        let newIndex = inputHistoryIndex;

        if (direction === "up") {
          if (inputHistoryIndex === -1) {
            // Starting navigation, save current input
            newIndex = 0;
          } else if (inputHistoryIndex < inputHistory.length - 1) {
            newIndex = inputHistoryIndex + 1;
          }
        } else {
          if (inputHistoryIndex > 0) {
            newIndex = inputHistoryIndex - 1;
          } else if (inputHistoryIndex === 0) {
            newIndex = -1;
          }
        }

        if (newIndex !== inputHistoryIndex) {
          const newInput = newIndex === -1 ? "" : inputHistory[newIndex];
          set((state) => ({
            sessions: state.sessions.map((s) =>
              s.id === activeSession.id
                ? { ...s, currentInput: newInput, inputHistoryIndex: newIndex }
                : s,
            ),
          }));
        }
      },

      // Evaluation
      evaluate: () => {
        const { getActiveSession, addToHistory, getEnvironment } = get();
        const activeSession = getActiveSession();
        if (!activeSession) return;

        const input = activeSession.currentInput;
        if (!input.trim()) return;

        track("calculated");

        const environment = getEnvironment();

        // Set up IO context for file operations
        const ioContext = createWorkspaceIOContext(activeSession.id);
        environment.setIOContext(ioContext);

        const result = safeExecute(input, environment);

        if (result.success) {
          const resultValue = result.result.value;

          // Check if result is an async operation that needs additional handling
          if (isAsyncOperation(resultValue)) {
            const asyncOp = resultValue as AsyncOperation;

            // Handle async operation
            handleAsyncOperation(asyncOp, ioContext, (name, value) =>
              environment.set(name, value),
            )
              .then((opResult) => {
                // Set loaded variables into environment if any
                if (opResult.variablesToSet) {
                  for (const [name, value] of Object.entries(
                    opResult.variablesToSet,
                  )) {
                    environment.set(name, value);
                  }
                }

                // Bind the resolved value to the assignment target, if this was
                // `x = csvread(...)` / `x = open()` and similar.
                const assignTo = (asyncOp as AsyncOperation & {
                  assignTo?: string;
                }).assignTo;
                if (assignTo && opResult.result !== null) {
                  environment.set(assignTo, opResult.result);
                }

                // Add to history with async result. When assigning, echo the
                // value (like a normal assignment); otherwise show the message.
                addToHistory({
                  input,
                  output: assignTo
                    ? formatAsyncResult(opResult.result)
                    : opResult.message || formatAsyncResult(opResult.result),
                  result: opResult.result,
                  type: getResultType(opResult.result),
                  plots: undefined,
                });
              })
              .catch((error) => {
                addToHistory({
                  input,
                  output:
                    error instanceof Error ? error.message : String(error),
                  result: null,
                  type: "null",
                  isError: true,
                  errorMessage:
                    error instanceof Error ? error.message : String(error),
                });
              });

            // Clear input immediately (async operation will add to history when done)
            set((state) => ({
              sessions: state.sessions.map((s) =>
                s.id === activeSession.id
                  ? {
                      ...s,
                      currentInput: "",
                      inputHistory: [
                        input,
                        ...s.inputHistory.filter((h) => h !== input),
                      ].slice(0, 100),
                      inputHistoryIndex: -1,
                    }
                  : s,
              ),
            }));
            return;
          }

          // Regular synchronous result
          addToHistory({
            input,
            output: result.result.display,
            result: result.result.value,
            type: result.result.type,
            latex: result.result.latex,
            plots: result.result.plots,
            segments: result.result.segments,
          });
        } else {
          addToHistory({
            input,
            output: result.error.message,
            result: null,
            type: "null",
            isError: true,
            errorMessage: result.error.message,
          });
        }

        // Add to input history
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === activeSession.id
              ? {
                  ...s,
                  currentInput: "",
                  inputHistory: [
                    input,
                    ...s.inputHistory.filter((h) => h !== input),
                  ].slice(0, 100),
                  inputHistoryIndex: -1,
                }
              : s,
          ),
        }));
      },

      clearInput: () => {
        const activeSession = get().getActiveSession();
        if (!activeSession) return;

        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === activeSession.id
              ? { ...s, currentInput: "", inputHistoryIndex: -1 }
              : s,
          ),
          cursorPosition: 0,
        }));
      },

      clearHistory: () => set({ history: [] }),

      clearSessionHistory: (sessionId?: string) => {
        const targetId = sessionId || get().activeSessionId;
        if (!targetId) return;

        set((state) => ({
          history: state.history.filter((h) => h.sessionId !== targetId),
          environments: {
            ...state.environments,
            [targetId]: createEnvironment(),
          },
        }));
      },

      // History actions
      addToHistory: (entry) => {
        const activeSession = get().getActiveSession();
        if (!activeSession) return;

        set((state) => ({
          history: [
            {
              ...entry,
              id: generateId(),
              timestamp: Date.now(),
              sessionId: activeSession.id,
            },
            ...state.history,
          ].slice(0, 5000), // Keep max 5000 entries total
        }));
      },

      deleteHistoryEntry: (id: string) => {
        set((state) => ({
          history: state.history.filter((entry) => entry.id !== id),
        }));
      },

      reloadHistoryEntry: (id: string) => {
        const { history, setInput, switchSession } = get();
        const entry = history.find((e) => e.id === id);
        if (entry) {
          // Switch to the entry's session if needed
          if (entry.sessionId !== get().activeSessionId) {
            switchSession(entry.sessionId);
          }
          setInput(entry.input);
        }
      },

      // Sequence actions
      createSequence: (name, entryIds) => {
        const id = generateId();
        const now = Date.now();
        set((state) => ({
          sequences: [
            ...state.sequences,
            {
              id,
              name,
              entries: entryIds,
              createdAt: now,
              updatedAt: now,
            },
          ],
        }));
      },

      deleteSequence: (id) => {
        set((state) => ({
          sequences: state.sequences.filter((seq) => seq.id !== id),
        }));
      },

      runSequence: (id: string) => {
        const { sequences, history, getActiveSession } = get();
        const sequence = sequences.find((s) => s.id === id);
        const activeSession = getActiveSession();
        if (!sequence || !activeSession) return;

        // Reset environment and run each entry in sequence
        const newEnv = createEnvironment();

        for (const entryId of sequence.entries) {
          const entry = history.find((h) => h.id === entryId);
          if (entry && !entry.isError) {
            try {
              execute(entry.input, newEnv);
            } catch {
              // Continue even if one fails
            }
          }
        }

        set((state) => ({
          environments: {
            ...state.environments,
            [activeSession.id]: newEnv,
          },
        }));
      },

      // UI actions
      setShowHistory: (show: boolean) => set({ showHistory: show }),
      setShowPlot: (show: boolean) => set({ showPlot: show }),
      setActiveTab: (tab: CalculatorState["activeTab"]) =>
        set({ activeTab: tab }),
      setSessionSidebarCollapsed: (collapsed: boolean) =>
        set({ sessionSidebarCollapsed: collapsed }),
      setShowArchivedSessions: (show: boolean) =>
        set({ showArchivedSessions: show }),

      togglePlotCollapsed: (id: string) => {
        const { collapsedPlots } = get();
        if (collapsedPlots.includes(id)) {
          set({ collapsedPlots: collapsedPlots.filter((i) => i !== id) });
        } else {
          set({ collapsedPlots: [...collapsedPlots, id] });
        }
      },

      isPlotCollapsed: (id: string) => {
        return get().collapsedPlots.includes(id);
      },

      // Search
      setSearchQuery: (query: string) => set({ searchQuery: query }),
      setSearchScope: (scope: "all" | "current") => set({ searchScope: scope }),
      setSearchDateFrom: (date: string) => set({ searchDateFrom: date }),
      setSearchDateTo: (date: string) => set({ searchDateTo: date }),
      setSearchErrorsOnly: (enabled: boolean) =>
        set({ searchErrorsOnly: enabled }),
      setSearchPlotsOnly: (enabled: boolean) =>
        set({ searchPlotsOnly: enabled }),

      getFilteredHistory: () => {
        const {
          history,
          searchQuery,
          searchScope,
          activeSessionId,
          searchDateFrom,
          searchDateTo,
          searchErrorsOnly,
          searchPlotsOnly,
        } = get();

        let filtered = history;

        if (searchScope === "current" && activeSessionId) {
          filtered = filtered.filter((h) => h.sessionId === activeSessionId);
        }

        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase();
          filtered = filtered.filter(
            (h) =>
              h.input.toLowerCase().includes(query) ||
              h.output.toLowerCase().includes(query),
          );
        }

        if (searchDateFrom) {
          const from = new Date(searchDateFrom).getTime();
          filtered = filtered.filter((h) => h.timestamp >= from);
        }

        if (searchDateTo) {
          const to = new Date(searchDateTo).getTime() + 86400000; // end of day
          filtered = filtered.filter((h) => h.timestamp < to);
        }

        if (searchErrorsOnly) {
          filtered = filtered.filter((h) => h.isError);
        }

        if (searchPlotsOnly) {
          filtered = filtered.filter((h) => h.type === "plot");
        }

        return filtered;
      },

      resetEnvironment: () => {
        const activeSession = get().getActiveSession();
        if (!activeSession) return;

        set((state) => ({
          environments: {
            ...state.environments,
            [activeSession.id]: createEnvironment(),
          },
        }));
      },

      getEnvironment: () => {
        const { environments, getActiveSession } = get();
        const activeSession = getActiveSession();
        if (!activeSession) return createEnvironment();

        if (!environments[activeSession.id]) {
          const newEnv = createEnvironment();
          set((state) => ({
            environments: {
              ...state.environments,
              [activeSession.id]: newEnv,
            },
          }));
          return newEnv;
        }

        return environments[activeSession.id];
      },
    }),
    {
      name: "quanta-storage",
      partialize: (state) => ({
        sessions: state.sessions,
        activeSessionId: state.activeSessionId,
        history: state.history,
        sequences: state.sequences,
        collapsedPlots: state.collapsedPlots,
        sessionSidebarCollapsed: state.sessionSidebarCollapsed,
        showArchivedSessions: state.showArchivedSessions,
      }),
      onRehydrateStorage: () => (state: CalculatorState | undefined) => {
        // Ensure there's always at least one session after rehydration
        if (state && (!state.sessions || state.sessions.length === 0)) {
          const defaultSession = createDefaultSession();
          state.sessions = [defaultSession];
          state.activeSessionId = defaultSession.id;
        }

        // Ensure activeSessionId is valid
        if (state && state.sessions && state.activeSessionId) {
          if (
            !state.sessions.find((s: Session) => s.id === state.activeSessionId)
          ) {
            state.activeSessionId = state.sessions[0]?.id || null;
          }
        }

        // Migrate history entries without sessionId
        if (state && state.history && state.sessions) {
          const defaultSessionId = state.sessions[0]?.id;
          if (defaultSessionId) {
            state.history = state.history.map((h: HistoryEntry) => ({
              ...h,
              sessionId: h.sessionId || defaultSessionId,
            }));
          }
        }
      },
    },
  ),
);
